import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useActiveProfileTheme } from '@/hooks/useActiveProfileTheme';
import { useToast } from '@/hooks/useToast';
import { getAvatarShape } from '@/lib/emojiMask';
import {
  type CoreThemeColors,
  hslStringToHex,
  hexToHslString,
  deriveTokensFromCore,
  tokenToCssVar,
  THEME_PRESETS,
} from '@/lib/themeEngine';

import { EditProfileForm } from '@/components/EditProfileForm';
import { DittoAvatar } from '@/components/DittoAvatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Palette, User, Sparkles, Loader2, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

export default function SettingsPage() {
  const { user, metadata } = useCurrentUser();
  const navigate = useNavigate();

  useSeoMeta({
    title: 'Settings — Ditto Profile Demo',
    description: 'Edit your profile and customize your theme.',
  });

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-border/50 bg-card/80 backdrop-blur-sm">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <User className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-xl font-title font-bold">Sign in Required</h2>
            <p className="text-muted-foreground text-sm">
              You need to log in to edit your profile and theme settings.
            </p>
            <Button onClick={() => navigate('/')} className="rounded-full">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const fullMetadata = metadata ? { ...metadata } as Record<string, unknown> : undefined;
  const shape = getAvatarShape(fullMetadata);
  const displayName = metadata?.display_name || metadata?.name || 'Anonymous';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border/50 bg-card/30 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full shrink-0"
            onClick={() => {
              const npub = nip19.npubEncode(user.pubkey);
              navigate(`/${npub}`);
            }}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3 min-w-0">
            <DittoAvatar
              src={metadata?.picture}
              alt={displayName}
              shape={shape}
              size={36}
            />
            <div className="min-w-0">
              <h1 className="text-lg font-semibold truncate">Settings</h1>
              <p className="text-xs text-muted-foreground truncate">{displayName}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="w-full bg-muted/50 rounded-full p-1 mb-6">
            <TabsTrigger value="profile" className="flex-1 rounded-full gap-2 data-[state=active]:bg-background">
              <User className="w-4 h-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="theme" className="flex-1 rounded-full gap-2 data-[state=active]:bg-background">
              <Palette className="w-4 h-4" />
              Theme
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="font-title">Edit Profile</CardTitle>
                <CardDescription>
                  Update your Nostr profile information.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <EditProfileForm />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="theme">
            <ThemeEditor pubkey={user.pubkey} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ThemeEditor({ pubkey }: { pubkey: string }) {
  const { data: existingTheme, isLoading } = useActiveProfileTheme(pubkey);
  const { mutateAsync: publishEvent, isPending } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const defaultColors: CoreThemeColors = existingTheme ?? {
    background: '260 20% 6%',
    text: '240 20% 92%',
    primary: '270 70% 60%',
  };

  const [bgHex, setBgHex] = useState(hslStringToHex(defaultColors.background));
  const [textHex, setTextHex] = useState(hslStringToHex(defaultColors.text));
  const [primaryHex, setPrimaryHex] = useState(hslStringToHex(defaultColors.primary));

  // Update state when existing theme loads
  useEffect(() => {
    if (existingTheme) {
      setBgHex(hslStringToHex(existingTheme.background));
      setTextHex(hslStringToHex(existingTheme.text));
      setPrimaryHex(hslStringToHex(existingTheme.primary));
    }
  }, [existingTheme]);

  const currentColors: CoreThemeColors = {
    background: hexToHslString(bgHex),
    text: hexToHslString(textHex),
    primary: hexToHslString(primaryHex),
  };

  const applyPreset = (preset: typeof THEME_PRESETS[0]) => {
    setBgHex(hslStringToHex(preset.colors.background));
    setTextHex(hslStringToHex(preset.colors.text));
    setPrimaryHex(hslStringToHex(preset.colors.primary));
  };

  const publishTheme = async () => {
    try {
      await publishEvent({
        kind: 16767,
        content: '',
        tags: [
          ['c', bgHex, 'background'],
          ['c', textHex, 'text'],
          ['c', primaryHex, 'primary'],
          ['alt', 'Active profile theme'],
        ],
      });

      queryClient.invalidateQueries({ queryKey: ['activeProfileTheme', pubkey] });

      toast({
        title: 'Theme Published!',
        description: 'Your profile theme has been updated across the Nostr network.',
      });
    } catch (error) {
      console.error('Failed to publish theme:', error);
      toast({
        title: 'Error',
        description: 'Failed to publish your theme. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const removeTheme = async () => {
    try {
      // Publish a "blank" theme that effectively resets to default
      await publishEvent({
        kind: 5,
        content: '',
        tags: [['k', '16767']],
      });

      queryClient.invalidateQueries({ queryKey: ['activeProfileTheme', pubkey] });

      toast({
        title: 'Theme Removed',
        description: 'Your profile theme has been cleared.',
      });
    } catch (error) {
      console.error('Failed to remove theme:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove your theme.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
        <CardContent className="pt-6 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground mt-2">Loading your theme...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Presets */}
      <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="font-title flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Theme Presets
          </CardTitle>
          <CardDescription>
            Choose a starting point, then customize to your liking.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {THEME_PRESETS.map((preset) => (
              <button
                key={preset.name}
                onClick={() => applyPreset(preset)}
                className="group relative rounded-xl border border-border/50 overflow-hidden hover:border-primary/50 transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                <div className="flex h-12">
                  <div className="flex-1" style={{ backgroundColor: `hsl(${preset.colors.background})` }} />
                  <div className="w-4" style={{ backgroundColor: `hsl(${preset.colors.primary})` }} />
                  <div className="flex-1" style={{ backgroundColor: `hsl(${preset.colors.text})` }} />
                </div>
                <div className="p-2 text-center bg-card/80">
                  <span className="text-xs">{preset.emoji} {preset.name}</span>
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Color Pickers */}
      <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="font-title flex items-center gap-2">
            <Palette className="w-5 h-5 text-primary" />
            Custom Colors
          </CardTitle>
          <CardDescription>
            Pick your 3 core colors. Everything else is derived automatically.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <ColorPicker label="Background" value={bgHex} onChange={setBgHex} />
            <ColorPicker label="Text" value={textHex} onChange={setTextHex} />
            <ColorPicker label="Primary" value={primaryHex} onChange={setPrimaryHex} />
          </div>
        </CardContent>
      </Card>

      {/* Live Preview */}
      <Card className="border-border/50 overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="font-title text-base">Live Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <ThemePreview colors={currentColors} />
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          onClick={publishTheme}
          disabled={isPending}
          className="flex-1 rounded-full glow-primary"
        >
          {isPending ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Sparkles className="w-4 h-4 mr-2" />
          )}
          Publish Theme
        </Button>

        {existingTheme && (
          <Button
            variant="outline"
            onClick={removeTheme}
            className="rounded-full border-destructive/30 hover:bg-destructive/10 text-destructive"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function ColorPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (hex: string) => void;
}) {
  return (
    <div className="space-y-2">
      <label className="text-xs text-muted-foreground uppercase tracking-widest block text-center">
        {label}
      </label>
      <div className="relative group">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full aspect-square rounded-xl cursor-pointer border-2 border-border/50 hover:border-primary/50 transition-all appearance-none [&::-webkit-color-swatch-wrapper]:p-1 [&::-webkit-color-swatch]:rounded-lg [&::-webkit-color-swatch]:border-0"
        />
      </div>
      <p className="text-[10px] text-muted-foreground text-center font-mono">{value}</p>
    </div>
  );
}

function ThemePreview({ colors }: { colors: CoreThemeColors }) {
  const tokens = deriveTokensFromCore(colors.background, colors.text, colors.primary);
  const cssVars: Record<string, string> = {};
  for (const [key, value] of Object.entries(tokens)) {
    cssVars[tokenToCssVar(key)] = value;
  }

  return (
    <div className="rounded-xl overflow-hidden" style={cssVars}>
      <div className="p-4 space-y-3" style={{ backgroundColor: `hsl(${tokens.background})` }}>
        {/* Mini profile card preview */}
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-full"
            style={{ backgroundColor: `hsl(${tokens.primary})` }}
          />
          <div className="space-y-1">
            <div className="h-3 w-24 rounded-full" style={{ backgroundColor: `hsl(${tokens.foreground})` }} />
            <div className="h-2 w-16 rounded-full opacity-50" style={{ backgroundColor: `hsl(${tokens.mutedForeground})` }} />
          </div>
        </div>
        {/* Text preview */}
        <p className="text-sm" style={{ color: `hsl(${tokens.foreground})` }}>
          This is how your profile will look to visitors.
        </p>
        {/* Button preview */}
        <div className="flex gap-2">
          <div
            className="px-4 py-1.5 rounded-full text-xs font-medium"
            style={{
              backgroundColor: `hsl(${tokens.primary})`,
              color: `hsl(${tokens.primaryForeground})`,
            }}
          >
            Follow
          </div>
          <div
            className="px-4 py-1.5 rounded-full text-xs font-medium border"
            style={{
              borderColor: `hsl(${tokens.border})`,
              color: `hsl(${tokens.foreground})`,
            }}
          >
            Message
          </div>
        </div>
        {/* Border/card preview */}
        <div
          className="p-3 rounded-lg border"
          style={{
            borderColor: `hsl(${tokens.border})`,
            backgroundColor: `hsl(${tokens.card})`,
          }}
        >
          <p className="text-xs" style={{ color: `hsl(${tokens.mutedForeground})` }}>
            Card with derived colors
          </p>
        </div>
      </div>
    </div>
  );
}
